import Modal from '../../../components/Modal';
import { useState } from 'react';
import { BodyMedium, BodySmall, Heading4Text } from '../../../components/Texts';
import { BalanceContainer } from '../../../components/NavUserProfile';
import Button from '../../../components/Button';
import { openCenteredWindow } from '../../../utils/window';
import { useStartDepositMutation } from '../../../redux/api/authorized/deposit';
import { useStartWithdrawalMutation } from '../../../redux/api/authorized/withdrawal';
import styled from 'styled-components';
import TextInput from '../../../components/Inputs/Text';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  width: 100%;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 8px;
  border: 1px solid ${(props) => props.theme.border.primary};
  background: ${(props) =>
    props.active ? props.theme.primary.primary : 'transparent'};
  color: ${(props) =>
    props.active ? props.theme.background.primary : props.theme.text.primary};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const WalletBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const WalletModal = () => {
  const [solanaBalance, setSolanaBalance] = useState('0.00');
  const [vetsBalance, setVetsBalance] = useState('0.00');
  const [startDeposit] = useStartDepositMutation();
  const [startWithdrawal] = useStartWithdrawalMutation();
  const [solAmount, setSolAmount] = useState('');
  const [calculatedVets, setCalculatedVets] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [isManaging, setIsManaging] = useState(false);

  const handleDeposit = async () => {
    const onrampUrl = await startDeposit({ network: 'solana' }).unwrap();
    openCenteredWindow(onrampUrl);
  };

  const handleWithdraw = async () => {
    const onrampUrl = await startWithdrawal({ network: 'solana' }).unwrap();
    openCenteredWindow(onrampUrl);
  };

  const handleSolInputChange = (e) => {
    const value = e.target.value;
    setSolAmount(value);
    const vetsAmount = value ? (parseFloat(value) * 100).toFixed(2) : '';
    setCalculatedVets(vetsAmount);
  };

  // If they have no SOL, show only deposit option
  if (solanaBalance <= 0) {
    return (
      <Modal>
        <WalletBodyContainer>
          <Heading4Text style={{ marginBottom: '16px' }}>
            Get Started with VETS
          </Heading4Text>
          <BodyMedium style={{ marginBottom: '24px', textAlign: 'center' }}>
            Add funds to your wallet to prepare for the VETS token launch! It is
            important to purchase the funds ahead of time in case of any delay
            in your first crypto purchase from your bank.
          </BodyMedium>
          <Button onClick={handleDeposit} fullWidth={true}>
            Deposit
          </Button>
        </WalletBodyContainer>
      </Modal>
    );
  }

  return (
    <Modal>
      <WalletBodyContainer>
        {!isManaging ? (
          <>
            <BalanceContainer style={{ opacity: 0.8, padding: '8px' }}>
              <BodySmall>USD Balance</BodySmall>
              <BodySmall>${solanaBalance} </BodySmall>
              <ButtonContainer style={{ justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => setIsManaging(true)}
                  size='small'
                  variant='link'
                >
                  Manage
                </Button>
              </ButtonContainer>
            </BalanceContainer>

            <div style={{ padding: '16px 0' }}>
              <BalanceContainer
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <BodyMedium>VETS Balance</BodyMedium>
                <Heading4Text>{vetsBalance} VETS</Heading4Text>
              </BalanceContainer>

              {vetsBalance > 0 && (
                <TabContainer>
                  <Tab
                    active={activeAction === 'buy'}
                    onClick={() => setActiveAction('buy')}
                  >
                    Buy More
                  </Tab>
                  <Tab
                    active={activeAction === 'sell'}
                    onClick={() => setActiveAction('sell')}
                  >
                    Sell
                  </Tab>
                </TabContainer>
              )}

              {(vetsBalance <= 0 || activeAction === 'buy') && (
                <>
                  <TextInput
                    placeholder='$0'
                    style={{ marginBottom: '8px' }}
                    value={solAmount}
                    onChange={handleSolInputChange}
                    label='Buy Amount (in USD)'
                    type='number'
                  />
                  {calculatedVets && (
                    <BodySmall style={{ marginBottom: '16px', opacity: 0.8 }}>
                      You will receive {calculatedVets} VETS
                    </BodySmall>
                  )}
                  <Button
                    onClick={handleDeposit}
                    fullWidth={true}
                    variant='primary'
                    size='large'
                  >
                    Buy VETS
                  </Button>
                </>
              )}

              {activeAction === 'sell' && (
                <>
                  <TextInput
                    placeholder='0 VETS'
                    style={{ marginBottom: '8px' }}
                    label='Sell Amount (in VETS)'
                    type='number'
                  />
                  <Button
                    onClick={handleWithdraw}
                    fullWidth={true}
                    variant='primary'
                    size='large'
                  >
                    Sell VETS
                  </Button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <Heading4Text style={{ marginBottom: '16px' }}>
              Manage USD Balance
            </Heading4Text>
            <BalanceContainer
              style={{ opacity: 0.8, padding: '8px', marginBottom: '24px' }}
            >
              <BodySmall>Current Balance</BodySmall>
              <BodySmall>${solanaBalance}</BodySmall>
            </BalanceContainer>

            <ButtonContainer>
              <Button
                onClick={handleDeposit}
                variant='primary'
                style={{ flex: 1 }}
              >
                Deposit
              </Button>
              <Button
                onClick={handleWithdraw}
                variant='secondary'
                style={{ flex: 1 }}
              >
                Withdraw
              </Button>
            </ButtonContainer>

            <Button
              onClick={() => setIsManaging(false)}
              variant='link'
              style={{ marginTop: '16px' }}
            >
              Back to Wallet
            </Button>
          </>
        )}
      </WalletBodyContainer>
    </Modal>
  );
};

export default WalletModal;
